/* eslint-disable max-params */
import { CANCELED_BY_OPTIONS, CancellationReason, Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { User, UserShort } from '../../../types/user';
import { ExternalUser } from '../../../types/externalUser';
import { InitialHours } from '../../../pages/Reservations/Reservations';
import { middleOfDay } from '../../../utils/DateUtils';
import { CancellationForm, ReservationForm } from '../validators';
import { CancellationInfo, ClientType } from '../types';

export const getEmployeeBranchIds = (user?: User | UserShort): string[] | undefined => {
  if (!user) return undefined;
  return 'branches' in user ? user.branches.map((branch) => branch.id) : user.branchIds;
};

export const getInitialForm = (
  reservation?: Reservation,
  authUser?: User,
  isCreating: boolean = false,
  reservationType?: ReservationType,
  initialHours?: InitialHours,
  preselectedExternalUser?: ExternalUser,
  preselectedBranchId?: string,
  visible: boolean = false,
): ReservationForm => {
  if (!visible) return {};
  const { hasAssignedUsers } = reservationType ?? {};
  const {
    localStartDate,
    localEndDate,
    price,
    comment,
    reservationTypeId,
    user,
    externalUsers,
    confirmedExternalUsers,
    queuedExternalUsers,
    branch,
    customName,
    title,
    informativeUser,
  } = reservation ?? {};
  const initialDay = initialHours?.start ? new Date(initialHours.start) : middleOfDay(new Date());

  // Inicializar attendanceData basado en confirmedExternalUsers
  const initialAttendanceData =
    confirmedExternalUsers?.reduce(
      (acc, user) => ({
        ...acc,
        [user.id]: user.attendance ?? false,
      }),
      {},
    ) ?? {};

  const preselectedUserHasCoupon =
    !!preselectedExternalUser?.userCoupons &&
    preselectedExternalUser.userCoupons.some((coupon) => coupon.isActive && coupon.usesRemaining > 0);

  const preselectedBranch =
    isCreating && preselectedBranchId ? authUser?.branches?.find((b) => b.id === preselectedBranchId) : undefined;

  return {
    localStartDate: localStartDate ?? initialHours?.start,
    localEndDate: localEndDate ?? initialHours?.end,
    price: preselectedUserHasCoupon ? 0 : price,
    comment,
    reservationTypeId,
    user: hasAssignedUsers ? (isCreating ? authUser : user) : undefined,
    branch: isCreating ? preselectedBranch : branch,
    externalUsers: preselectedExternalUser ? [preselectedExternalUser] : externalUsers,
    confirmedExternalUsers,
    queuedExternalUsers,
    selectedDay: reservation?.localStartDate ? new Date(reservation.localStartDate) : initialDay,
    userCouponId:
      reservation?.userCouponId ?? (preselectedUserHasCoupon ? preselectedExternalUser?.userCoupons?.[0]?.id : undefined),
    useCoupon: !!reservation?.userCouponId || preselectedUserHasCoupon,
    unregisteredClientName: reservation?.unregisteredClient?.name,
    unregisteredClientSurnames: reservation?.unregisteredClient?.surnames,
    unregisteredClientPhone: reservation?.unregisteredClient?.phone,
    usersBeforeSave: [],
    attendanceData: initialAttendanceData,
    newCouponId: undefined,
    typeOfCouponToUse: undefined,
    selectedUserToShareCoupon: undefined,
    customName: reservation?.customName,
    title,
    informativeUser,
  };
};

export const getCancellationPayload = (
  cancellationForm: CancellationForm,
  isCancelingGroupWithMultipleUsers: boolean = false,
): CancellationInfo => {
  const canceledByAdmin =
    isCancelingGroupWithMultipleUsers || cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN;
  return {
    canceledBy: canceledByAdmin ? CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN : cancellationForm.canceledBy!,
    cancellationReason: canceledByAdmin ? CancellationReason.OTHER : cancellationForm.cancellationReason!,
    ...(cancellationForm.cancellationReason === CancellationReason.OTHER || canceledByAdmin
      ? { customCancellationReason: cancellationForm.customCancellationReason }
      : {}),
  };
};

export const getInitialClientType = (reservation: Reservation | undefined): ClientType => {
  if (!reservation) return 'unregistered';
  if (reservation.externalUsers?.length && reservation.externalUsers.length > 0) return 'registered';
  return 'unregistered';
};
